import React, { FC, useState } from 'react';
import styled from 'styled-components';
import OvTable from '../molecules/OvTable';
import OvNoContent from '../molecules/OvNoContent';
import { useTranslation } from 'react-i18next';
import { UserInfo } from '../../../common/model/dto/user-info';
import Colours from '../../../design-system/colours';
import OvButton from '../atoms/OvButton';
import OvDateRangePicker from '../molecules/OvDateRangePicker';
import OvLoadingIndicator from '../atoms/OvLoadingIndicator';
import { Range } from 'react-date-range';
import Popover from '@mui/material/Popover';
import OvRangePickerDisplay from '../molecules/OvRangePickerDisplay';
import OvDayStatusHighlight from '../atoms/OvDayStatusHighlight';
import { CalendarDayStatusUtils } from '../../../common/utils/services/calendar-day-status-utils';
import DateUtils from '../../../common/utils/services/date-utils';
import moment from 'moment';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Variables from '../../../design-system/variables';
import { DailyData } from '../../../common/model/dto/daily-data';
import { TableRowEditWidgetType } from '../../../common/model/ui/table-row-edit-widget.type';
import { Paginated } from '../../../common/types';
import breakpoints from '../../../design-system/breakpoints';
import { PdfReport } from '../../../common/model/dto/pdf-report';
import { PdfReportUtils } from '../../../common/utils/services/pdf-report-utils';
import { getPdfReports } from '../../../services/admin/admin-pdf-report.service';
import { loadDailyDataBetweenDateRange } from '../../../services/admin/admin-daily-data.service';
import { usePdfReportActions } from './hooks/usePdfReportActions';
import queryString from 'query-string';
import { startOfDay } from 'date-fns';
import { loadDailyDataBetweenDateRangeForPatient } from '../../../services/clinic-location.service';
import { getPdfReportsForPatient } from '../../../services/pdf-report.service';
import { useQuery } from '@tanstack/react-query';
import AdminCycleSelector from '../molecules/AdminCycleSelector';
import ReportModeToggle from '../molecules/ReportModeToggle';
import { UserCycle } from '../../../common/model/dto/user-cycle/user-cycle';

const OvUserDetailReports: FC<{
  selectedUser?: UserInfo;
  clinicLocationId?: string;
  currentUser?: UserInfo;
}> = ({ selectedUser, clinicLocationId, currentUser }) => {
  const { t } = useTranslation();
  const [cycleRange, setCycleRange] = useState<Range[]>([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection',
    },
  ]);
  const [cycleRangePickerAnchorEl, setCycleRangePickerAnchorEl] =
    useState(null);

  const [reportMode, setReportMode] = useState<'custom' | 'cycle'>('custom');
  const [selectedCycle, setSelectedCycle] = useState<UserCycle | null>(null);

  const {
    data: paginatedPdfReports,
    isPending: isPdfReportsLoading,
    isFetching: isPdfReportsFetching,
    error: pdfReportsError,
    refetch,
  } = useQuery({
    queryKey: ['pdf-reports', selectedUser?.id, clinicLocationId],
    queryFn: () => {
      if (clinicLocationId && selectedUser?.id) {
        return getPdfReportsForPatient(
          clinicLocationId,
          selectedUser.id,
          queryString.stringify({
            limit: 0,
            offset: 0,
            order_by: 'created_at',
            order_dir: 'desc',
          })
        );
      } else if (selectedUser?.id) {
        return getPdfReports(
          queryString.stringify({
            limit: 0,
            offset: 0,
            order_by: 'created_at',
            order_dir: 'desc',
            user_id: selectedUser.id,
          })
        );
      }
      return Promise.resolve({
        docs: [],
        total: 0,
        limit: 0,
        offset: 0,
      } as Paginated<PdfReport>);
    },
    enabled: !!selectedUser,
  });

  const pdfReports = React.useMemo(
    () => paginatedPdfReports?.docs ?? [],
    [paginatedPdfReports?.docs]
  );

  const [dailyDataListInCalendar, setDailyDataListInCalendar] = useState<
    DailyData[]
  >([]);

  const handleNavigationBetweenMonths = async (
    date: Date,
    userDocumentId: string
  ) => {
    const firstVisibleDay = moment(date)
      .startOf('month')
      .subtract(20, 'days') // 7days needed because of the layout of the calendar, and another 10 day to count post ovulation days
      .toDate();
    const lastVisibleDay = moment(date)
      .add(1, 'month')
      .endOf('month')
      .add(7, 'days')
      .toDate();

    if (clinicLocationId) {
      const paginatedDailyData = await loadDailyDataBetweenDateRangeForPatient(
        userDocumentId,
        clinicLocationId,
        queryString.stringify({
          limit: 0,
          user_document_id: userDocumentId,
          clinic_location_id: clinicLocationId,
          order_by: 'day',
          order_dir: 'desc',
          day_from: DateUtils.getDbDateTag(startOfDay(firstVisibleDay)),
          day_to: DateUtils.getDbDateTag(startOfDay(lastVisibleDay)),
        })
      );

      setDailyDataListInCalendar(paginatedDailyData.docs);
    } else {
      const paginatedDailyData = await loadDailyDataBetweenDateRange(
        queryString.stringify({
          limit: 0,
          user_document_id: userDocumentId,
          day_from: DateUtils.getDbDateTag(startOfDay(firstVisibleDay)),
          day_to: DateUtils.getDbDateTag(startOfDay(lastVisibleDay)),
        })
      );

      setDailyDataListInCalendar(paginatedDailyData.docs);
    }
  };

  const {
    createReport,
    updateReport,
    deleteReport,
    isLoading: isBackendRequestLoading,
    errorMessage,
    clearError,
  } = usePdfReportActions({ clinicLocationId, selectedUser, refetch });

  const onCreateNewReport = async () => {
    if (reportMode === 'custom') {
      if (cycleRange[0].startDate && cycleRange[0].endDate && selectedUser) {
        createReport(selectedUser, {
          day_from: DateUtils.getDbDateTag(cycleRange[0].startDate),
          day_to: DateUtils.getDbDateTag(cycleRange[0].endDate),
          pdf_report_type: 'custom-interval-report',
        });
      }
    } else {
      if (selectedCycle && selectedUser) {
        createReport(selectedUser, {
          day_from: selectedCycle.start_day,
          day_to: selectedCycle.end_day || DateUtils.getDbDateTag(new Date()),
          pdf_report_type: 'cycle-report',
        });
      }
    }
  };

  const reportListTableData = React.useMemo(() => {
    return PdfReportUtils.mapPdfReportsToTableData(pdfReports).map(
      (tableRow) => ({
        ...tableRow,
        deleteOptions:
          !clinicLocationId ||
          pdfReports.find((pdfReport) => pdfReport.id === tableRow.rowId)
            ?.created_by?.id === currentUser?.id
            ? {
                confirmationOptions: {
                  title: t('pdfReports.removeConfirmTitle'),
                  description: t('pdfReports.removeConfirmDescription'),
                },
                deleteAction: async () => {
                  if (tableRow.rowId) {
                    await deleteReport(tableRow.rowId);
                  }
                },
              }
            : undefined,
        editOptions:
          !clinicLocationId ||
          pdfReports.find((pdfReport) => pdfReport.id === tableRow.rowId)
            ?.created_by?.id === currentUser?.id
            ? {
                widgetType: TableRowEditWidgetType.TEXT,
                propertyName: 'title',
                placeholder: t('pdfReports.title'),
              }
            : undefined,
      })
    );
  }, [pdfReports, clinicLocationId, currentUser, t, deleteReport]);

  const getLogIndexFromDate = (calendarDate: Date): number => {
    if (!dailyDataListInCalendar.length) return -1;

    return dailyDataListInCalendar.findIndex((dailyData) => {
      const dailyDataDate = moment(dailyData?.day, 'YYYY-MM-DD').toDate();
      return DateUtils.areSameDays(dailyDataDate, calendarDate);
    });
  };

  const onOpenCycleRangePicker = (event: any) => {
    setCycleRangePickerAnchorEl(event.currentTarget);

    if (selectedUser?.document_id) {
      const shownDate = cycleRange[0].startDate || new Date();
      handleNavigationBetweenMonths(shownDate, selectedUser.document_id);
    }
  };

  const handleCloseCycleRangePicker = () => {
    setCycleRangePickerAnchorEl(null);
  };

  const onShownDateChanged = (date: Date) => {
    if (date && selectedUser?.document_id) {
      handleNavigationBetweenMonths(date, selectedUser.document_id);
    }
  };

  const isCycleRangePickerOpened = Boolean(cycleRangePickerAnchorEl);
  const popoverId = isCycleRangePickerOpened ? 'simple-popover' : undefined;

  return (
    <>
      <Header>
        <ReportModeToggle
          mode={reportMode}
          onChange={(newMode) => {
            setReportMode(newMode);
            clearError();
          }}
        />

        {reportMode === 'custom' && (
          <RangePickerWrapper>
            <OvRangePickerDisplay
              aria-describedby={popoverId}
              startTitle={t('common.cycleStart')}
              endTitle={t('common.cycleEnd')}
              startPlaceholder={t('pdfReports.startDatePlaceholder')}
              endPlaceholder={t('pdfReports.endDatePlaceholder')}
              startDate={cycleRange[0].startDate}
              endDate={cycleRange[0].endDate}
              handleClick={onOpenCycleRangePicker}
            />
            <StyledPopover
              id={popoverId}
              open={isCycleRangePickerOpened}
              anchorEl={cycleRangePickerAnchorEl}
              onClose={handleCloseCycleRangePicker}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
            >
              <OvDateRangePicker
                ranges={cycleRange}
                onChange={(item) => setCycleRange([item.selection])}
                onShownDateChange={(date) => onShownDateChanged(date)}
                showDateDisplay={false}
                moveRangeOnFirstSelection={false}
                months={2}
                direction="horizontal"
                preventSnapRefocus={true}
                showPreview={true}
                showMonthAndYearPickers={false}
                rangeColors={['#dfdfdf']}
                dayContentRenderer={(date) => (
                  <OvDayStatusHighlight
                    tryingToConceiveStatus={CalendarDayStatusUtils.calculateDayStatus(
                      dailyDataListInCalendar,
                      getLogIndexFromDate(date)
                    )}
                  >
                    {date.getDate()}
                  </OvDayStatusHighlight>
                )}
              />
            </StyledPopover>
          </RangePickerWrapper>
        )}

        {reportMode === 'cycle' && (
          <AdminCycleSelector
            userId={selectedUser?.id}
            selectedCycleId={selectedCycle?.id || ''}
            onCycleSelect={(cycle) => setSelectedCycle(cycle)}
            enabled={reportMode === 'cycle'}
          />
        )}

        <StyledButton
          disabled={
            reportMode === 'custom'
              ? !cycleRange[0]?.startDate || !cycleRange[0]?.endDate
              : !selectedCycle
          }
          onClick={onCreateNewReport}
        >
          {t('pdfReports.create')}
        </StyledButton>
      </Header>

      {errorMessage ? (
        <StyledErrorMessage>
          <StyledErrorOutlineIcon />
          {errorMessage}
        </StyledErrorMessage>
      ) : (
        ''
      )}

      {pdfReportsError ? (
        <StyledErrorMessage>
          <StyledErrorOutlineIcon />
          {pdfReportsError.message}
        </StyledErrorMessage>
      ) : (
        ''
      )}

      {pdfReports?.length ? (
        <OvTable
          data={reportListTableData}
          onSaveRowValue={updateReport}
          getIdFromRowData={true}
          editable={true}
        />
      ) : (
        ''
      )}

      {(isPdfReportsLoading || isBackendRequestLoading) && (
        <OvLoadingIndicator position="fixed" />
      )}

      {!pdfReports?.length &&
      (!isPdfReportsLoading ||
        !isBackendRequestLoading ||
        !isPdfReportsFetching) ? (
        <OvNoContent>{t('pdfReports.emptyList')}</OvNoContent>
      ) : (
        ''
      )}
    </>
  );
};

export default OvUserDetailReports;

const Header = styled.header`
  display: flex;
  align-items: center;
  border-radius: 0.5rem;
  background-color: ${Colours.OV_BASE};
  color: ${Colours.OV_SEMI_LIGHT};
  margin-bottom: 1.5rem;

  @media (max-width: ${breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
    padding-bottom: 0.5rem;
  }
`;

const RangePickerWrapper = styled.div`
  flex-grow: 1;
  margin-right: 2rem;
  padding: 0.5rem 0.5rem 0.5rem 2rem;

  @media (max-width: ${breakpoints.md}) {
    margin-right: 0;
    padding: 0.5rem 1rem;
  }
`;

const StyledButton = styled(OvButton)`
  && {
    text-transform: none;
    padding: 0.25rem 0.75rem;
    margin: 0.5rem;
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    color: ${Colours.OV_BASE};
    background-color: ${Colours.OV_WHITE};
    &:hover {
      opacity: 0.9;
    }

    @media (max-width: ${breakpoints.md}) {
      margin: 0.5rem 1rem;
    }
  }
`;

const StyledErrorMessage = styled.p`
  color: ${Colours.OV_RED};
  margin: 10px 0 24px 0;
  display: flex;
  align-items: center;
`;

const StyledErrorOutlineIcon = styled(ErrorOutlineIcon)`
  margin-right: 0.5rem;
`;

const StyledPopover = styled(Popover)`
  && {
    .MuiPopover-paper {
      border-radius: 0.75rem;
      border: 1px solid ${Colours.OV_BASE};
    }
  }
`;
